// Types that cannot be opened inside a chrome browser
const restrictedTypes = [
  'application/vnd.ms-powerpoint',
  'application/octet-stream',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const displayFileOrDownload = (blob: Blob, fileName: string): void => {
  const blobType = blob.type;
  const data = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = data;
  link.download = fileName;
  if (restrictedTypes.includes(blobType)) {
    link.click();
    setTimeout(() => {
      // For Firefox it is necessary to delay revoking the ObjectURL
      window.URL.revokeObjectURL(data);
    }, 100);
  } else {
    window.open(data);
  }
};

const alternativeFileDownload = (blob: Blob, fileName: string): void => {
  const data = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = data;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => {
    window.URL.revokeObjectURL(data);
  }, 100);
};

/**
 * Converts an array of objects to CSV and triggers a download in the browser.
 * @param data Array of objects to convert
 * @param fileName Name of the CSV file to download
 * @param fields Array of object keys to include in the CSV (order matters)
 */
function exportArrayToCSV<T extends Record<string, string | number>>(
  data: T[],
  fileName: string,
  fields: { fieldKey: string; name: string }[],
): void {
  if (!data || !data.length) return;
  const keys =
    fields && fields.length
      ? fields.map((f) => f.fieldKey)
      : Object.keys(data[0]);

  const fieldNameMap = new Map(
    (fields || []).map((item) => [item.fieldKey, item.name]),
  );

  const csvRows = [
    keys.map((k) => fieldNameMap.get(k) || k).join(','),
    ...data.map((row) =>
      keys
        .map((k) => {
          const val = row[k as keyof T] ?? '';
          // Escape quotes and wrap in quotes if needed
          const escaped = String(val).replace(/"/g, '""');
          return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        })
        .join(','),
    ),
  ];

  // Add UTF-8 BOM for better compatibility with Excel and Hebrew characters
  const BOM = '\uFEFF';
  const csvContent = BOM + csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  alternativeFileDownload(blob, fileName);
}

export { displayFileOrDownload, exportArrayToCSV };
